import { DataLoader } from 'src/app/services/data-loader/data-loader';
import {
    DataLoaderResult,
    DataLoaderResultStatus,
} from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardUsersActivity } from '../sql-maps/biviz-dashboard-users-activity-datasource-sql-map';

/**
 * Options for the UsersdashActivityMonthlyDataLoaderFactory.
 * @interface
 * @extends ICreatedInstanceId
 * @extends IGcpProjectId
 * @extends IQueryCacheService
 */
export interface UsersdashActivityMonthlyDataLoaderFactoryOptions
    extends ICreatedInstanceId,
        IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId | (() => GcpProjectId);
    queryCacheService: QueryCacheService;
}

/** This class generates DataLoader instance for analytics vs store reports differences */
export class UsersdashActivityMonthlyDataLoaderFactory extends Factory<
    DataLoader,
    UsersdashActivityMonthlyDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string =
        'UsersdashActivityMonthlyDataLoaderFactory';

    getInstance(options: UsersdashActivityMonthlyDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            // Resolve gcpProjectId to always be a value
            const resolvedGcpProjectId: GcpProjectId =
                typeof options.gcpProjectId === 'function'
                    ? options.gcpProjectId()
                    : options.gcpProjectId;

            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceSqlMap = BiVizDashboardUsersActivity.sqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    datasourceSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                // TODO add context settings. Remember to update chart options build logic in order to support dynamic choice of day_no
                // const maxDayNo =
                //     queryContext.settings[
                //         QueryContextSettingID.ENGAGEMENT_MAX_DAY_NO
                //     ] ?? 30;
                // const selectedDayNoSQL: Partial<ICompiledSQLTokens> = {
                //     where: [`s_day_no <= ${maxDayNo}`],
                // };

                const {
                    dateTrunc,
                    splitSelect,
                    splitGroupBy,
                    where,
                    // is_in_context_app_version,
                    // unnestDatasourceColumn,
                    join,
                } = BiVizDashboardUsersActivity.compileSQLStatements(
                    dsColumns.present,
                    queryContext.settings,
                    { gcpProjectId: resolvedGcpProjectId }
                    // selectedDayNoSQL
                );

                // const selectFacts = is_in_context_app_version
                //     ? 'ds.date, ds.proper_id, app_version.*,'
                //     : 'ds.*,';
                const splitGroupByJoinCondition = splitSelect
                    ? 'AND base.split_group = sum_dau.split_group'
                    : '';

                const query = `
-- DataLoaderId: ${this.instanceId}
WITH base AS (
    SELECT ${splitSelect}date, month, user_id, d0_user_id
    FROM \`${generateTableFullPath(
        resolvedGcpProjectId,
        datasourceSqlMap.dataset,
        datasourceSqlMap.tableId
    )}\` ds
      ${join}
      ${where}
)

, sum_dau AS (
    SELECT 
    ${splitGroupBy}month AS date,
    SUM(dau) AS sum_dau
    FROM
    (
      SELECT ${splitGroupBy}month, date,
      COUNT(DISTINCT user_id) AS dau
      FROM base
      GROUP BY ${splitGroupBy}month,date
    )
    GROUP BY ${splitGroupBy}month
  )
  
SELECT 
  base.*,
  sum_dau.sum_dau / dates AS avg_monthly_dau
FROM
(
  SELECT ${splitGroupBy}month AS date,
  COUNT(DISTINCT user_id) AS mau,
  COUNT(DISTINCT d0_user_id) AS new_users,
  COUNT(DISTINCT date) AS dates
  FROM base
  GROUP BY ${splitGroupBy}month
) base
JOIN sum_dau ON base.date = sum_dau.date
${splitGroupByJoinCondition}
ORDER BY ${splitGroupBy}base.date ASC`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
